import { IonContent, IonItem, IonLabel, IonModal } from "@ionic/react";
import React, { useState, useEffect, useRef } from "react";
import { Helmet } from "react-helmet";

const objects = [
    { name: "Basket", full: "basket-full.png", empty: "basket-empty.png" },
    { name: "Shopping Cart", full: "cart-full.png", empty: "cart-empty.png" },
    { name: "School Bag", full: "bag-full.png", empty: "bag-empty.png" },
    { name: "Fridge", full: "fridge-full.png", empty: "fridge-empty.png" },
    { name: "Toy Box", full: "toybox-full.png", empty: "toybox-empty.png" },
    { name: "Bookshelf", full: "bookshelf-full.png", empty: "bookshelf-empty.png" },
    { name: "Wallet", full: "wallet-full.png", empty: "wallet-empty.png" },
    { name: "Water Bottle", full: "bottle-full.png", empty: "bottle-empty.png" },
    { name: "Classroom", full: "classroom-full.png", empty: "classroom-empty.png" },
];

const FullEmpty: React.FC = () => {
    const [question, setQuestion] = useState("");
    const [target, setTarget] = useState("long");
    const [selectedObject, setSelectedObject] = useState(objects[0]);
    const [message, setMessage] = useState("");
    const [showImage, setShowImage] = useState(false);
    const modal = useRef<HTMLIonModalElement>(null);

    useEffect(() => {
        generateQuestion();
        document.title = "Full and Empty – LKG Pre-Math Worksheet | Mama’s Guidance"; // Set page title
    }, []);

    const generateQuestion = () => {
        const randomObject = objects[Math.floor(Math.random() * objects.length)];
        const randomTarget = Math.random() > 0.5 ? "full" : "empty";

        setSelectedObject(randomObject);
        setTarget(randomTarget);
        setQuestion(`Which one is ${randomTarget}?`);
        setMessage("");
    };

    const checkAnswer = (choice: string) => {
        if (choice === target) {
            setShowImage(true);
            setTimeout(() => {
                generateQuestion()
                setShowImage(false);
                setMessage("")
            }, 5000)
        } else {
            setShowImage(false);
            setMessage("❌ Oops! Try again.");
        }
    };

    return (
        <>
            <Helmet>
                <meta name="description" content='Help LKG learners understand the concept of full and empty with interactive activities on Mama’s Guidance—designed to enhance early observation and comparison skills through fun and engaging visual exercises.' />
            </Helmet>
            {
                showImage === false ?
                    <>
                        <div className="contentPart additionWord pickColor matchImage match-container game-container">
                            <div className="showInfo" style={{ width: '80%' }} onClick={() => modal.current?.present()}><i className="fas fa-info-circle"></i></div>
                            <IonModal
                                ref={modal}
                                trigger="open-modal"
                                initialBreakpoint={1}
                                breakpoints={[0, 0.5, 0.75, 1]}
                                handleBehavior="cycle"
                                backdropDismiss={false}  // ✅ Prevents click-to-close
                                className="full-page-modal"
                            >
                                <IonContent className="ion-padding">
                                    <div style={{ textAlign: 'right', fontSize: '25px' }} onClick={() => modal.current?.dismiss()}><i className="fas fa-3a fa-times"></i></div>
                                    <h1>Full and Empty</h1>

                                    <p>
                                        Introduce your child to the basic math concept of <strong>full and empty</strong> with this engaging and interactive worksheet. Designed for LKG learners, it uses clear visuals and real-life scenarios to help children easily understand the difference between containers that are full and those that are empty.
                                    </p>

                                    <h2>🥤 What Does Full and Empty Mean?</h2>
                                    <p>
                                        “<strong>Full</strong>” means something has no more space inside — it is completely filled. “<strong>Empty</strong>” means there is nothing inside. This foundational concept helps children start comparing quantities and preparing for volume and measurement learning.
                                    </p>

                                    <h2>🧩 What’s in the Worksheet?</h2>
                                    <ul>
                                        <li>Picture-based comparisons (e.g., <strong>full vs. empty glass, basket, or jar</strong>)</li>
                                        <li>Circle the full or empty container</li>
                                        <li>Match full objects with empty versions</li>
                                        <li>Coloring activities to identify and distinguish between <strong>full and empty items</strong></li>
                                    </ul>

                                    <h2>🎯 Learning Objectives</h2>
                                    <ul>
                                        <li>Understand the concept of <strong>full and empty</strong></li>
                                        <li>Improve observation and comparison skills</li>
                                        <li>Build descriptive vocabulary (<strong>full, empty, filled, vacant</strong>)</li>
                                        <li>Lay the foundation for future learning on volume and capacity</li>
                                    </ul>

                                    <h2>👩‍🏫 How to Use the Worksheet</h2>
                                    <ul>
                                        <li>Point at the images and ask: <strong>“Which one is full? Which one is empty?”</strong></li>
                                        <li>Let your child circle or color the correct pictures.</li>
                                        <li>Use water bottles, cups, or boxes at home to demonstrate the idea practically.</li>
                                        <li>Play a sorting game — group toys into full or empty boxes.</li>
                                    </ul>


                                    <h2>🔗 Related Topics</h2>
                                    <div>
                                        <IonItem routerLink="/lkg/mathematics/pre-math-concepts/long-short"
                                            routerDirection="none"
                                            lines="none"
                                            detail={true}>
                                            <IonLabel>Long - Short</IonLabel>
                                        </IonItem>
                                        <IonItem routerLink="/lkg/mathematics/pre-math-concepts/big-small"
                                            routerDirection="none"
                                            lines="none"
                                            detail={true}>
                                            <IonLabel>Big - Small</IonLabel>
                                        </IonItem>
                                        <IonItem routerLink="/lkg/mathematics/pre-math-concepts/more-less"
                                            routerDirection="none"
                                            lines="none"
                                            detail={true}>
                                            <IonLabel>More - Less</IonLabel>
                                        </IonItem>
                                    </div>

                                    <h2>🧠 Why This Concept Is Important</h2>
                                    <p>
                                        Learning full and empty develops early <strong>visual reasoning</strong> and <strong>measurement awareness</strong>. It helps children recognize <strong>differences in volume, laying the foundation for capacity, comparison, and basic math operations</strong>.
                                    </p>

                                    <div className="btn hero-btn" onClick={() => modal.current?.dismiss()}>Close</div>
                                </IonContent>
                            </IonModal>
                            <article className="visually-hidden">
                                <h1>Full and Empty</h1>

                                <p>
                                    Introduce your child to the basic math concept of <strong>full and empty</strong> with this engaging and interactive worksheet. Designed for LKG learners, it uses clear visuals and real-life scenarios to help children easily understand the difference between containers that are full and those that are empty.
                                </p>

                                <h2>🥤 What Does Full and Empty Mean?</h2>
                                <p>
                                    “<strong>Full</strong>” means something has no more space inside — it is completely filled. “<strong>Empty</strong>” means there is nothing inside. This foundational concept helps children start comparing quantities and preparing for volume and measurement learning.
                                </p>

                                <h2>🧩 What’s in the Worksheet?</h2>
                                <ul>
                                    <li>Picture-based comparisons (e.g., <strong>full vs. empty glass, basket, or jar</strong>)</li>
                                    <li>Circle the full or empty container</li>
                                    <li>Match full objects with empty versions</li>
                                    <li>Coloring activities to identify and distinguish between <strong>full and empty items</strong></li>
                                </ul>

                                <h2>🎯 Learning Objectives</h2>
                                <ul>
                                    <li>Understand the concept of <strong>full and empty</strong></li>
                                    <li>Improve observation and comparison skills</li>
                                    <li>Build descriptive vocabulary (<strong>full, empty, filled, vacant</strong>)</li>
                                    <li>Lay the foundation for future learning on volume and capacity</li>
                                </ul>

                                <h2>👩‍🏫 How to Use the Worksheet</h2>
                                <ul>
                                    <li>Point at the images and ask: <strong>“Which one is full? Which one is empty?”</strong></li>
                                    <li>Let your child circle or color the correct pictures.</li>
                                    <li>Use water bottles, cups, or boxes at home to demonstrate the idea practically.</li>
                                    <li>Play a sorting game — group toys into full or empty boxes.</li>
                                </ul>


                                <h2>🔗 Related Topics</h2>
                                <div>
                                    <IonItem routerLink="/lkg/mathematics/pre-math-concepts/long-short"
                                        routerDirection="none"
                                        lines="none"
                                        detail={true}>
                                        <IonLabel>Long - Short</IonLabel>
                                    </IonItem>
                                    <IonItem routerLink="/lkg/mathematics/pre-math-concepts/big-small"
                                        routerDirection="none"
                                        lines="none"
                                        detail={true}>
                                        <IonLabel>Big - Small</IonLabel>
                                    </IonItem>
                                    <IonItem routerLink="/lkg/mathematics/pre-math-concepts/more-less"
                                        routerDirection="none"
                                        lines="none"
                                        detail={true}>
                                        <IonLabel>More - Less</IonLabel>
                                    </IonItem>
                                </div>

                                <h2>🧠 Why This Concept Is Important</h2>
                                <p>
                                    Learning full and empty develops early <strong>visual reasoning</strong> and <strong>measurement awareness</strong>. It helps children recognize differences in volume, laying the foundation for capacity, comparison, and basic math operations.
                                </p>
                            </article>
                            <h2>{question}</h2>
                            <div className="mt-5 mb-5" style={{ display: "flex", justifyContent: "center", gap: "20px" }}>
                                <img src={process.env.REACT_APP_API_URL + selectedObject.full} alt="Full Object" style={{ width: "100px" }} onClick={() => checkAnswer("full")} />
                                <img src={process.env.REACT_APP_API_URL + selectedObject.empty} alt="Empty Object" style={{ width: "100px" }} onClick={() => checkAnswer("empty")} />
                            </div>
                            <p>{message}</p></div>
                    </>
                    :
                    <>
                        <div className="contentPart showMiddle">
                            <img style={{ height: '150px' }} className='img-fluid' src={process.env.REACT_APP_WELDONE_LINK} />
                        </div>
                    </>
            }
        </>
    );
};

export default FullEmpty;
